import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Gauge } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: "navigator", label: "Nav Hobbies", accent: "text-violet-700", showGroupCap: false },
  { key: "intensive", label: "Nav Intensives", accent: "text-fuchsia-700", showGroupCap: false },
  { key: "mariner_boys", label: "Boys Mar Hobbies", accent: "text-blue-700", showGroupCap: true },
  { key: "mariner_girls", label: "Girls Mar Hobbies", accent: "text-pink-700", showGroupCap: true },
];

export default function CapacityAccordion({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/activity-caps`, { headers, params: { ts: Date.now() } });
      setData(r.data);
      setDraft({});
    } catch (e) {
      toast.error("Failed to load capacities");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rowKey = (program, activity) => `${program}::${activity}`;

  const saveRow = async (program, row) => {
    const key = rowKey(program, row.activity);
    const d = draft[key] || {};
    setSavingKey(key);
    try {
      await axios.put(`${API}/admin/activity-caps`, {
        program_key: program,
        activity: row.activity,
        per_week_cap: d.per_week_cap ?? row.per_week_cap,
        per_group_cap: d.per_group_cap !== undefined ? d.per_group_cap : row.per_group_cap,
        disabled_weeks: row.disabled_weeks ?? [],
      }, { headers });
      toast.success(`${row.activity} capacity saved`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSavingKey(null);
  };

  return (
    <Card data-testid="settings-capacity-accordion">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Gauge className="w-5 h-5 text-amber-600" /> Activity Capacities
        </CardTitle>
        <CardDescription className="text-xs">
          Per-activity weekly max. Changes apply immediately to hobby/intensive selection.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {!data ? (
          <p className="text-xs text-muted-foreground p-2">Loading…</p>
        ) : (
          <Accordion type="multiple" className="w-full">
            {SECTIONS.map(({ key, label, accent, showGroupCap }) => (
              <AccordionItem key={key} value={key}>
                <AccordionTrigger className={`text-sm font-semibold ${accent}`} data-testid={`capacity-accordion-${key}`}>
                  {label}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-1.5">
                    {(data[key] || []).map(row => {
                      const k = rowKey(key, row.activity);
                      const d = draft[k] || {};
                      const perWeek = d.per_week_cap ?? row.per_week_cap;
                      const perGroup = d.per_group_cap !== undefined ? d.per_group_cap : row.per_group_cap;
                      const dirty = Object.keys(d).length > 0;
                      const slug = row.activity.replace(/\s+/g, "-");
                      return (
                        <div key={row.activity} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-stone-50" data-testid={`capacity-row-${key}-${slug}`}>
                          <span className="text-xs font-medium flex-1 truncate">{row.activity}</span>
                          <Input
                            type="number" min="1" max="500"
                            value={perWeek}
                            onChange={e => setDraft(dr => ({ ...dr, [k]: { ...(dr[k] || {}), per_week_cap: parseInt(e.target.value, 10) || 0 } }))}
                            className="w-16 h-7 text-center text-xs"
                            title="Max per week"
                            data-testid={`capacity-input-${key}-${slug}`}
                          />
                          {showGroupCap && (
                            <Input
                              type="number" min="1" max="50"
                              value={perGroup ?? ""}
                              placeholder="grp"
                              onChange={e => {
                                const v = e.target.value.trim();
                                setDraft(dr => ({ ...dr, [k]: { ...(dr[k] || {}), per_group_cap: v === "" ? null : (parseInt(v, 10) || 0) } }));
                              }}
                              className="w-14 h-7 text-center text-xs"
                              title="Per-group sub-cap (blank = none)"
                              data-testid={`capacity-groupcap-${key}-${slug}`}
                            />
                          )}
                          <Button
                            size="sm"
                            variant={dirty ? "default" : "outline"}
                            disabled={!dirty || savingKey === k}
                            onClick={() => saveRow(key, row)}
                            className="h-7 px-2 text-xs"
                            data-testid={`capacity-save-${key}-${slug}`}
                          >
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}
